import React, { useState } from 'react';
import { useColorMode } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaUserPlus, FaWheelchair, FaBaby, FaHome, FaCheckCircle, FaArrowRight, FaUndo, FaPiggyBank, FaStar } from 'react-icons/fa';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';

const SimulateurAides = () => {
    const { colorMode } = useColorMode();
    const isDark = colorMode === 'dark';

    const [step, setStep] = useState(1);
    const [profile, setProfile] = useState(null); // 'senior', 'handicap', 'enfant', 'confort'
    const [condition, setCondition] = useState(null);
    const [results, setResults] = useState([]);

    // --- LOGIQUE DU SIMULATEUR ---
    const handleProfileSelect = (selectedProfile) => {
        setProfile(selectedProfile);
        if (selectedProfile === 'confort') {
            // Si c'est juste du ménage de confort, on passe direct aux résultats
            calculateResults(selectedProfile, null);
        } else {
            setStep(2);
        }
    };

    const handleConditionSelect = (selectedCondition) => {
        setCondition(selectedCondition);
        calculateResults(profile, selectedCondition);
    };

    const calculateResults = (prof, cond) => {
        let eligibleAids = [];
        
        // Tous les profils ont droit au crédit d'impôt
        eligibleAids.push({
            title: "Crédit d'impôt de 50%",
            desc: "L'État prend en charge 50% de vos dépenses liées aux services à la personne, déductibles de vos impôts ou remboursés directement.",
            icon: FaPiggyBank,
            color: "text-green-500",
            bg: "bg-green-50 dark:bg-green-900/20"
        });

        if (prof === 'senior' && cond === 'dependant') {
            eligibleAids.unshift({
                title: "APA (Allocation Personnalisée d'Autonomie)",
                desc: "Destinée aux personnes de 60 ans et plus en perte d'autonomie (GIR 1 à 4). Elle aide à payer les dépenses nécessaires pour rester à domicile.",
                icon: FaStar,
                color: "text-yellow-500",
                bg: "bg-yellow-50 dark:bg-yellow-900/20"
            });
        }

        if (prof === 'handicap' && cond === 'oui') {
            eligibleAids.unshift({
                title: "PCH (Prestation de Compensation du Handicap)",
                desc: "Aide financière versée par le département pour rembourser les dépenses liées à la perte d'autonomie des personnes en situation de handicap.",
                icon: FaWheelchair,
                color: "text-blue-500",
                bg: "bg-blue-50 dark:bg-blue-900/20"
            });
        }

        if (prof === 'enfant' && cond === 'moins_de_6') {
            eligibleAids.unshift({
                title: "CMG (Complément de Libre Choix du Mode de Garde)",
                desc: "Une aide de la CAF versée jusqu'aux 6 ans de votre enfant pour financer une partie des frais de garde à domicile.",
                icon: FaBaby,
                color: "text-pink-500",
                bg: "bg-pink-50 dark:bg-pink-900/20"
            });
        }

        setResults(eligibleAids);
        setStep(3);
    };

    const resetSimulateur = () => {
        setStep(1);
        setProfile(null);
        setCondition(null);
        setResults([]);
    };

    // --- ANIMATIONS ---
    const containerVariants = {
        hidden: { opacity: 0, x: 50 },
        visible: { opacity: 1, x: 0, transition: { duration: 0.4 } },
        exit: { opacity: 0, x: -50, transition: { duration: 0.3 } }
    };

    return (
        <section className={`py-16 md:py-24 px-4 max-w-4xl mx-auto overflow-hidden`}>
            <Helmet>
              <title>Simulateur d'aides financières — Hetep Iaout Services</title>
              <meta name="description" content="Estimez en quelques clics les aides financières auxquelles vous avez droit pour financer votre aide à domicile : APA, PCH, crédit d'impôt et plus encore." />
              <link rel="canonical" href="https://www.hisvie.com/simulateur-aides/" />
            </Helmet>
            <div className="text-center mb-12">
                <h2 className={`text-3xl md:text-5xl font-black mb-4 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>
                    Testez votre <span className="text-blue-600 font-serif italic font-light">éligibilité</span>
                </h2>
                <p className={`text-lg md:text-xl ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                    Répondez à ces questions rapides pour découvrir les aides auxquelles vous avez droit.
                </p>
            </div>

            <div className={`relative rounded-[2.5rem] p-6 md:p-12 shadow-2xl border ${isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-blue-50'}`}>
                
                {/* Barre de progression */}
                <div className="flex justify-center gap-2 mb-10">
                    <div className={`h-2 w-16 rounded-full transition-colors ${step >= 1 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
                    <div className={`h-2 w-16 rounded-full transition-colors ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
                    <div className={`h-2 w-16 rounded-full transition-colors ${step >= 3 ? 'bg-yellow-400' : 'bg-gray-200 dark:bg-gray-700'}`}></div>
                </div>

                <AnimatePresence mode="wait">
                    
                    {/* ÉTAPE 1 : PROFIL */}
                    {step === 1 && (
                        <motion.div key="step1" variants={containerVariants} initial="hidden" animate="visible" exit="exit" className="text-center">
                            <h3 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Pour qui cherchez-vous une aide ?</h3>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <button onClick={() => handleProfileSelect('senior')} className={`p-6 rounded-2xl border-2 flex flex-col items-center gap-4 transition-all hover:-translate-y-1 ${isDark ? 'border-gray-700 hover:border-blue-500 bg-gray-900/50' : 'border-gray-100 hover:border-blue-600 bg-gray-50'}`}>
                                    <FaUserPlus size={32} className="text-blue-600" />
                                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Personne âgée (+ de 60 ans)</span>
                                </button>
                                <button onClick={() => handleProfileSelect('handicap')} className={`p-6 rounded-2xl border-2 flex flex-col items-center gap-4 transition-all hover:-translate-y-1 ${isDark ? 'border-gray-700 hover:border-blue-500 bg-gray-900/50' : 'border-gray-100 hover:border-blue-600 bg-gray-50'}`}>
                                    <FaWheelchair size={32} className="text-green-500" />
                                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Personne en situation de handicap</span>
                                </button>
                                <button onClick={() => handleProfileSelect('enfant')} className={`p-6 rounded-2xl border-2 flex flex-col items-center gap-4 transition-all hover:-translate-y-1 ${isDark ? 'border-gray-700 hover:border-blue-500 bg-gray-900/50' : 'border-gray-100 hover:border-blue-600 bg-gray-50'}`}>
                                    <FaBaby size={32} className="text-pink-500" />
                                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Garde d'enfant(s)</span>
                                </button>
                                <button onClick={() => handleProfileSelect('confort')} className={`p-6 rounded-2xl border-2 flex flex-col items-center gap-4 transition-all hover:-translate-y-1 ${isDark ? 'border-gray-700 hover:border-blue-500 bg-gray-900/50' : 'border-gray-100 hover:border-blue-600 bg-gray-50'}`}>
                                    <FaHome size={32} className="text-yellow-500" />
                                    <span className={`font-bold ${isDark ? 'text-white' : 'text-gray-800'}`}>Moi-même (Ménage, Confort)</span>
                                </button>
                            </div>
                        </motion.div>
                    )}

                    {/* ÉTAPE 2 : CONDITIONS SPÉCIFIQUES */}
                    {step === 2 && profile === 'senior' && (
                        <motion.div key="step2-senior" variants={containerVariants} initial="hidden" animate="visible" exit="exit" className="text-center">
                            <h3 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>La personne est-elle en perte d'autonomie (GIR 1 à 4) ?</h3>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button onClick={() => handleConditionSelect('dependant')} className="flex-1 py-4 px-8 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700">Oui, elle a besoin d'assistance</button>
                                <button onClick={() => handleConditionSelect('autonome')} className={`flex-1 py-4 px-8 border-2 font-bold rounded-2xl ${isDark ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-100'}`}>Non, elle est autonome</button>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && profile === 'handicap' && (
                        <motion.div key="step2-handi" variants={containerVariants} initial="hidden" animate="visible" exit="exit" className="text-center">
                            <h3 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Le handicap est-il reconnu par la MDPH ?</h3>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button onClick={() => handleConditionSelect('oui')} className="flex-1 py-4 px-8 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700">Oui</button>
                                <button onClick={() => handleConditionSelect('non')} className={`flex-1 py-4 px-8 border-2 font-bold rounded-2xl ${isDark ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-100'}`}>Non / En cours</button>
                            </div>
                        </motion.div>
                    )}

                    {step === 2 && profile === 'enfant' && (
                        <motion.div key="step2-enfant" variants={containerVariants} initial="hidden" animate="visible" exit="exit" className="text-center">
                            <h3 className={`text-2xl font-bold mb-8 ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Quel est l'âge du plus jeune enfant à garder ?</h3>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button onClick={() => handleConditionSelect('moins_de_6')} className="flex-1 py-4 px-8 bg-blue-600 text-white font-bold rounded-2xl hover:bg-blue-700">Moins de 6 ans</button>
                                <button onClick={() => handleConditionSelect('plus_de_6')} className={`flex-1 py-4 px-8 border-2 font-bold rounded-2xl ${isDark ? 'border-gray-600 text-white hover:bg-gray-700' : 'border-gray-200 text-gray-700 hover:bg-gray-100'}`}>6 ans ou plus</button>
                            </div>
                        </motion.div>
                    )}

                    {/* ÉTAPE 3 : RÉSULTATS */}
                    {step === 3 && (
                        <motion.div key="step3" variants={containerVariants} initial="hidden" animate="visible" exit="exit">
                            <div className="text-center mb-8">
                                <div className="w-16 h-16 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                    <FaCheckCircle size={32} />
                                </div>
                                <h3 className={`text-2xl md:text-3xl font-black ${isDark ? 'text-white' : 'text-[#1B365D]'}`}>Bonne nouvelle !</h3>
                                <p className={`mt-2 ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>Voici les aides dont vous pouvez bénéficier :</p>
                            </div>

                            <div className="space-y-4 mb-10">
                                {results.map((res, i) => (
                                    <div key={i} className={`p-6 rounded-2xl border ${isDark ? 'bg-gray-900 border-gray-700' : 'bg-white border-gray-100 shadow-sm'} flex gap-6 items-start`}>
                                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${res.bg} ${res.color}`}>
                                            <res.icon size={20} />
                                        </div>
                                        <div>
                                            <h4 className={`text-lg font-bold mb-2 ${isDark ? 'text-white' : 'text-gray-800'}`}>{res.title}</h4>
                                            <p className={`text-sm leading-relaxed ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>{res.desc}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>

                            <div className="flex flex-col sm:flex-row justify-between items-center gap-4">
                                <button onClick={resetSimulateur} className={`flex items-center gap-2 font-medium ${isDark ? 'text-gray-400 hover:text-white' : 'text-gray-500 hover:text-[#1B365D]'}`}>
                                    <FaUndo /> Recommencer
                                </button>
                                <Link to="/demande-de-devis">
                                    <button className="bg-yellow-400 text-[#1B365D] font-black py-4 px-8 rounded-xl shadow-lg hover:bg-yellow-300 transition-colors flex items-center gap-2">
                                        Calculer mon devis exact <FaArrowRight />
                                    </button>
                                </Link>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </section>
    );
};

export default SimulateurAides;